// client/src/context/ThemeProvider.jsx - UPDATED FOR 11 PROPERTIES
import React, { createContext, useContext, useEffect, useState } from 'react';

const ThemeContext = createContext(null);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
};

// ============================================================
// COLOR UTILITIES (Built-in)
// ============================================================

function hexToRgb(hex) {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16)
  } : null;
}

function rgbToHex(r, g, b) {
  return "#" + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
}

function darken(hex, amount) {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;
  
  const r = Math.max(0, Math.floor(rgb.r * (1 - amount)));
  const g = Math.max(0, Math.floor(rgb.g * (1 - amount)));
  const b = Math.max(0, Math.floor(rgb.b * (1 - amount)));
  
  return rgbToHex(r, g, b);
}

function lighten(hex, amount) {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;
  
  const r = Math.min(255, Math.floor(rgb.r + (255 - rgb.r) * amount));
  const g = Math.min(255, Math.floor(rgb.g + (255 - rgb.g) * amount));
  const b = Math.min(255, Math.floor(rgb.b + (255 - rgb.b) * amount));
  
  return rgbToHex(r, g, b);
}

function hexToRgba(hex, opacity) {
  const rgb = hexToRgb(hex);
  if (!rgb) return `rgba(0, 0, 0, ${opacity})`;
  return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${opacity})`;
}

// Map enums to CSS values
const BUTTON_RADIUS = { pill: '999px', rounded: '8px', square: '4px' };
const CARD_RADIUS = { rounded: '12px', square: '4px' };
const CARD_SHADOW = {
  none: 'none',
  subtle: '0 2px 8px rgba(0, 0, 0, 0.04)',
  medium: '0 6px 18px rgba(11, 27, 42, 0.06)',
  bold: '0 12px 32px rgba(0, 0, 0, 0.12)'
};

// ============================================================
// THEME PROVIDER
// ============================================================

export const ThemeProvider = ({ children, tenantId = 'demo-university' }) => {
  const [theme, setTheme] = useState(null);
  const [branding, setBranding] = useState(null);
  const [loading, setLoading] = useState(true);

  // Fetch theme from API
  useEffect(() => {
    const fetchTheme = async () => {
      try {
        const API_ROOT = process.env.REACT_APP_API_URL || 'http://localhost:5000';
        const response = await fetch(`${API_ROOT}/api/tenant/${tenantId}/theme?app=quiz`);
        
        if (!response.ok) throw new Error('Failed to fetch theme');
        
        const data = await response.json();
        console.log('[ThemeProvider] Fetched theme:', data.theme);
        
        setTheme(data.theme);
        setBranding(data.branding);
        applyTheme(data.theme);
        
      } catch (error) {
        console.error('[ThemeProvider] Error:', error);
        
        // Use defaults on error
        const defaultTheme = {
          brandColor: '#0b5fff',
          buttonTextColor: '#ffffff',        // ✅ ADDED - Manual control
          backgroundColor: '#ffffff',
          backgroundAccent: '#f8fafc',
          textColor: '#0b1b2a',
          textSecondary: '#64748b',
          buttonStyle: 'rounded',
          cardStyle: 'rounded',
          cardShadow: 'medium',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
          headingFontFamily: 'inherit'
        };
        setTheme(defaultTheme);
        applyTheme(defaultTheme);
      } finally {
        setLoading(false);
      }
    };

    fetchTheme();
  }, [tenantId]);

  // Apply theme to CSS variables
  const applyTheme = (t) => {
    const root = document.documentElement;
    
    // ============================================================
    // UPDATED: Now includes buttonTextColor (11 properties total)
    // Matches backend Tenant model
    // ============================================================
    
    const {
      brandColor = '#0b5fff',
      buttonTextColor = '#ffffff',        // ✅ ADDED - Manual button text color
      backgroundColor = '#ffffff',
      backgroundAccent = '#f8fafc',
      textColor = '#0b1b2a',
      textSecondary = '#64748b',
      buttonStyle = 'rounded',
      cardStyle = 'rounded',
      cardShadow = 'medium',
      fontFamily = '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      headingFontFamily = 'inherit'
    } = t;
    
    // PRIMARY COLORS (from brandColor)
    root.style.setProperty('--mwc-primary', brandColor);
    root.style.setProperty('--mwc-primary-hover', darken(brandColor, 0.1));
    root.style.setProperty('--mwc-primary-light', lighten(brandColor, 0.9));
    root.style.setProperty('--mwc-primary-contrast', buttonTextColor); // ✅ UPDATED - Use manual value
    
    // Opacity variants
    root.style.setProperty('--mwc-primary-5', hexToRgba(brandColor, 0.05));
    root.style.setProperty('--mwc-primary-10', hexToRgba(brandColor, 0.1));
    root.style.setProperty('--mwc-primary-20', hexToRgba(brandColor, 0.2));
    root.style.setProperty('--mwc-primary-30', hexToRgba(brandColor, 0.3));
    
    // BACKGROUNDS
    root.style.setProperty('--mwc-surface', backgroundColor);
    root.style.setProperty('--mwc-bg', backgroundColor);
    root.style.setProperty('--mwc-bg-light', backgroundAccent);
    root.style.setProperty('--mwc-bg-lighter', darken(backgroundAccent, 0.05));
    
    // TEXT
    root.style.setProperty('--mwc-text', textColor);
    root.style.setProperty('--mwc-text-secondary', textSecondary);
    root.style.setProperty('--mwc-muted', textSecondary);
    root.style.setProperty('--mwc-text-light', lighten(textSecondary, 0.2));
    
    // BORDERS (auto-calculated from background)
    root.style.setProperty('--mwc-border-color', darken(backgroundColor, 0.1));
    root.style.setProperty('--mwc-border-color-hover', darken(backgroundColor, 0.15));
    
    // LAYOUT
    root.style.setProperty('--mwc-radius-sm', BUTTON_RADIUS[buttonStyle] || '8px');
    root.style.setProperty('--mwc-radius', CARD_RADIUS[cardStyle] || '12px');
    root.style.setProperty('--mwc-radius-lg', CARD_RADIUS[cardStyle] || '12px');
    root.style.setProperty('--mwc-card-shadow', CARD_SHADOW[cardShadow] || CARD_SHADOW.medium);
    
    // TYPOGRAPHY
    root.style.setProperty('--mwc-font-family', fontFamily);
    root.style.setProperty('--mwc-heading-font-family', headingFontFamily);
    
    console.log('[ThemeProvider] Applied theme to CSS:', {
      brandColor,
      buttonTextColor,                    // ✅ ADDED - Log button text color
      buttonStyle,
      cardStyle,
      cardShadow
    });
  };

  if (loading) {
    return <div>Loading theme...</div>;
  }

  return (
    <ThemeContext.Provider value={{ 
      theme, 
      branding, 
      setTheme: (newTheme) => {
        setTheme(newTheme);
        applyTheme(newTheme);
      }
    }}>
      {children}
    </ThemeContext.Provider>
  );
};

export default ThemeProvider;